import React, { createContext, useCallback, useContext, useRef, useState } from "react";

// ─── Context ────────────────────────────────────────────────────────────────
const ToastContext = createContext(null);

// ─── Provider ───────────────────────────────────────────────────────────────
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const addToast = useCallback((message, type = "info", duration = 3500) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");

  const { addToast } = ctx;
  return {
    success: (msg, duration) => addToast(msg, "success", duration),
    error:   (msg, duration) => addToast(msg, "error",   duration ?? 5000),
    warning: (msg, duration) => addToast(msg, "warning", duration),
    info:    (msg, duration) => addToast(msg, "info",    duration),
  };
}

// ─── Container ───────────────────────────────────────────────────────────────
function ToastContainer({ toasts, onRemove }) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onRemove={onRemove} />
      ))}
    </div>
  );
}

// ─── Item ────────────────────────────────────────────────────────────────────
const STYLES = {
  success: {
    border: "border-l-success",
    icon: "fas fa-check-circle text-success",
    bg: "bg-success-bg border-success-border",
  },
  error: {
    border: "border-l-danger",
    icon: "fas fa-exclamation-circle text-danger",
    bg: "bg-danger-bg border-danger-border",
  },
  warning: {
    border: "border-l-warning",
    icon: "fas fa-exclamation-triangle text-warning",
    bg: "bg-warning-bg border-warning-border",
  },
  info: {
    border: "border-l-info",
    icon: "fas fa-info-circle text-info",
    bg: "bg-info-bg border-info-border",
  },
};

function ToastItem({ toast, onRemove }) {
  const s = STYLES[toast.type] ?? STYLES.info;
  return (
    <div
      className={`pointer-events-auto flex items-start gap-3 rounded-xl shadow-prodia-md border border-l-4 ${s.border} ${s.bg} px-4 py-3 min-w-[280px] max-w-sm animate-slide-in-from-top bg-prodia-surface`}
    >
      <i className={`${s.icon} text-base flex-shrink-0 mt-0.5`} aria-hidden="true"></i>
      <span className="text-sm text-prodia-text-primary flex-1 leading-snug">{toast.message}</span>
      <button
        onClick={() => onRemove(toast.id)}
        className="text-prodia-text-muted hover:text-prodia-text-secondary flex-shrink-0 mt-0.5 transition-colors"
        aria-label="閉じる"
      >
        <i className="fas fa-times text-xs"></i>
      </button>
    </div>
  );
}
